/**
 * French Open Collection Page Enhancements
 * SEO-Safe UX improvements - JavaScript only, no HTML changes
 *
 * Features:
 * 1. Sticky "Jump to Date" navigation
 * 2. Sticky date headers while scrolling
 * 3. Filter count display
 * 4. Back to top button
 * 5. Smooth scroll enhancements
 */

(function() {
  'use strict';

  // Only run on collection pages with ticket cards
  if (!document.querySelector('.day-heading')) {
    return;
  }

  console.log('[Collection Enhancements] Initializing...');

  // =====================================================
  // 1. STICKY JUMP TO DATE NAVIGATION
  // =====================================================
  function createJumpToDateNav() {
    const dayHeadings = document.querySelectorAll('.day-heading');
    if (dayHeadings.length === 0) return;

    // Create the sticky nav container
    const stickyNav = document.createElement('div');
    stickyNav.className = 'jump-to-date-nav';
    stickyNav.innerHTML = `
      <div class="jump-to-date-inner">
        <label for="date-selector" class="jump-to-date-label">Jump to:</label>
        <select id="date-selector" class="jump-to-date-select">
          <option value="">Select a date...</option>
        </select>
        <span class="current-date-display"></span>
      </div>
    `;

    // Populate the dropdown with dates
    const select = stickyNav.querySelector('#date-selector');
    dayHeadings.forEach((heading, index) => {
      const dateText = heading.textContent.split('(')[0].trim();
      const option = document.createElement('option');
      option.value = index;
      option.textContent = dateText;

      // Add data attribute for anchor linking
      if (heading.id) {
        option.dataset.anchor = heading.id;
      }

      select.appendChild(option);
    });

    // Handle selection
    select.addEventListener('change', function() {
      const index = this.value;
      if (index === '') return;

      const heading = dayHeadings[index];
      if (heading) {
        const offset = 120; // Account for sticky nav
        const y = heading.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        this.value = ''; // Reset selection
      }
    });

    // Insert after the filter buttons
    const filterButtons = document.querySelector('.ticket-filters');
    if (filterButtons) {
      filterButtons.parentNode.insertBefore(stickyNav, filterButtons.nextSibling);
    } else {
      // Fallback: insert at the beginning of the custom HTML section
      const customSection = document.querySelector('.day-heading')?.parentElement;
      if (customSection) {
        customSection.insertBefore(stickyNav, customSection.firstChild);
      }
    }

    return stickyNav;
  }

  // =====================================================
  // 2. STICKY DATE HEADERS
  // =====================================================
  function setupStickyDateHeaders() {
    const dayHeadings = document.querySelectorAll('.day-heading');
    const currentDateDisplay = document.querySelector('.current-date-display');

    if (dayHeadings.length === 0) return;

    let ticking = false;

    function updateCurrentDate() {
      const scrollTop = window.pageYOffset;
      let currentHeading = null;

      dayHeadings.forEach(heading => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          currentHeading = heading;
        }
      });

      if (currentHeading && currentDateDisplay) {
        const dateText = currentHeading.textContent.split('(')[0].trim();
        // Only show short date (e.g., "May 24" instead of "Sunday, May 24, 2026")
        const shortDate = dateText.replace(/^[A-Za-z]+,\s*/, '').replace(/,\s*\d{4}$/, '');
        currentDateDisplay.textContent = shortDate;
        currentDateDisplay.style.opacity = '1';
      } else if (currentDateDisplay) {
        currentDateDisplay.style.opacity = '0';
      }

      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(updateCurrentDate);
        ticking = true;
      }
    });
  }

  // =====================================================
  // 3. FILTER COUNT DISPLAY
  // =====================================================
  function setupFilterCounts() {
    const filterButtons = document.querySelectorAll('.ticket-filters button');
    if (filterButtons.length === 0) return;

    // Create filter result display
    const filterResult = document.createElement('div');
    filterResult.className = 'filter-result-display';
    filterResult.style.display = 'none';

    const filterContainer = document.querySelector('.ticket-filters');
    if (filterContainer) {
      filterContainer.appendChild(filterResult);
    }

    // Count cards for each filter
    function countVisibleCards(filter) {
      const allCards = document.querySelectorAll('.ticket-card');
      const allGrids = document.querySelectorAll('.ticket-grid');

      if (filter === 'all' || filter === '#tickets') {
        return { cards: allCards.length, sessions: allGrids.length };
      }

      // For specific filters, count cards in the target section
      const targetId = filter.replace('#', '');
      const targetHeading = document.getElementById(targetId);

      if (!targetHeading) {
        // Count based on card content
        let count = 0;
        allCards.forEach(card => {
          const extraText = card.querySelector('.ticket-extra')?.textContent.toLowerCase() || '';
          if (filter === '#finals' && extraText.includes('final') && !extraText.includes('semi')) {
            count++;
          } else if (filter === '#semifinals' && extraText.includes('semi')) {
            count++;
          } else if (filter === '#quarterfinals' && extraText.includes('quarter')) {
            count++;
          }
        });
        return { cards: count, sessions: Math.ceil(count / 2) };
      }

      return { cards: allCards.length, sessions: allGrids.length };
    }

    // Add click handlers to show counts
    filterButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        const href = this.getAttribute('onclick')?.match(/location.href='([^']+)'/)?.[1] || '#tickets';
        const counts = countVisibleCards(href);

        filterResult.textContent = `Showing ${counts.cards} tickets across ${counts.sessions} sessions`;
        filterResult.style.display = 'block';

        // Hide after 3 seconds
        setTimeout(() => {
          filterResult.style.display = 'none';
        }, 3000);

        // Update active state
        filterButtons.forEach(b => b.classList.remove('active'));
        this.classList.add('active');
      });
    });
  }

  // =====================================================
  // 4. BACK TO TOP BUTTON
  // =====================================================
  function createBackToTopButton() {
    const button = document.createElement('button');
    button.className = 'back-to-top-btn';
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M18 15l-6-6-6 6"/>
      </svg>
      <span>Top</span>
    `;
    button.setAttribute('aria-label', 'Back to top');

    document.body.appendChild(button);

    // Show/hide based on scroll position
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(function() {
          if (window.pageYOffset > 500) {
            button.classList.add('visible');
          } else {
            button.classList.remove('visible');
          }
          ticking = false;
        });
        ticking = true;
      }
    });

    // Scroll to top on click
    button.addEventListener('click', function() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // =====================================================
  // 5. ENHANCED FILTER BUTTON SMOOTH SCROLL
  // =====================================================
  function enhanceFilterButtons() {
    const filterButtons = document.querySelectorAll('.ticket-filters button');

    filterButtons.forEach(button => {
      const onclickAttr = button.getAttribute('onclick');
      if (onclickAttr && onclickAttr.includes('location.href')) {
        const href = onclickAttr.match(/location.href='([^']+)'/)?.[1];

        if (href && href.startsWith('#')) {
          // Replace onclick with smooth scroll
          button.removeAttribute('onclick');
          button.addEventListener('click', function(e) {
            e.preventDefault();

            const targetId = href.replace('#', '');
            const target = document.getElementById(targetId);

            if (target) {
              const offset = 120;
              const y = target.getBoundingClientRect().top + window.pageYOffset - offset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            } else if (href === '#tickets') {
              // Scroll to first day heading
              const firstDay = document.querySelector('.day-heading');
              if (firstDay) {
                const offset = 120;
                const y = firstDay.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }
          });
        }
      }
    });
  }

  // =====================================================
  // INITIALIZE ALL FEATURES
  // =====================================================
  function init() {
    createJumpToDateNav();
    setupStickyDateHeaders();
    setupFilterCounts();
    createBackToTopButton();
    enhanceFilterButtons();

    console.log('[Collection Enhancements] All features initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
